import { Component, Input, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { CoachingThresholdComponent } from '@app-asset-config/components/coaching-threshold/coaching-threshold.component';
import { CoachingConfig, GetCoachingConfigResponse, PostCoachingConfigResponse } from '@app-asset-config/models/tagging.model';
import { TaggingService } from '@app-asset-config/services/tagging.service';
import { StorageService } from '@app-core/services/storage/storage.service';
import { EVENTS_CONFIG } from '@app-core/constants/constants';
import { DataService } from '@app-core/services/data/data.service';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { GoogleTagManagerService } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import { TranslateService } from '@ngx-translate/core';
import {
  DEFAULT_INCIDENTS_PER_100_MILES,
  DEFAULT_COACHING_INTERVAL,
  MINIMUM_EVENT_THREASHOLD,
} from '@app-asset-config/constants/config.constants';

@Component({
  selector: 'app-coaching-config-tab',
  templateUrl: './coaching-config-tab.component.html',
  styleUrls: ['./coaching-config-tab.component.scss'],
})
export class CoachingConfigTabComponent implements OnInit, OnDestroy {
  @Input() public fleetId: string;

  @ViewChild(CoachingThresholdComponent, { static: true })
  public coachingThreshold: CoachingThresholdComponent;
  public coachConfigLoader: boolean = true;
  public eventTypes: string[];
  public incidentsPer100Miles: number = DEFAULT_INCIDENTS_PER_100_MILES;
  public coachingInterval: number = DEFAULT_COACHING_INTERVAL;
  public minimumEventThreshold: number = MINIMUM_EVENT_THREASHOLD;
  public loader: boolean = false;
  public enableAutoSelection: boolean;
  public maxAutoSelectedEvents: number;
  public autoCoachingLoader: boolean = false;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private taggingService: TaggingService,
    private storageService: StorageService,
    public dataService: DataService,
    private snachBarService: SnackBarService,
    private gtmService: GoogleTagManagerService,
    public translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.getCoachingConfig();
  }

  private getCoachingConfig() {
    this.coachConfigLoader = true;
    this.storageService.setStorageValue('coachingConfig', {});
    const params = {
      fleetId: this.fleetId,
    };
    this.taggingService
      .getFleetCoachConfig(params)
      .pipe(
        finalize(() => {
          this.coachConfigLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: GetCoachingConfigResponse) => {
          const {
            eventTypes = Object.keys(EVENTS_CONFIG),
            incidentsPer100Miles = DEFAULT_INCIDENTS_PER_100_MILES,
            coachingInterval = DEFAULT_COACHING_INTERVAL,
            minimumEventThreshold = MINIMUM_EVENT_THREASHOLD,
            enableAutoSelection,
            maxAutoSelectedEvents,
          } = res?.value?.coachingConfig || {};

          this.eventTypes = eventTypes;
          this.incidentsPer100Miles = incidentsPer100Miles;
          this.coachingInterval = coachingInterval;
          this.minimumEventThreshold = minimumEventThreshold;
          this.enableAutoSelection = enableAutoSelection;
          this.maxAutoSelectedEvents = maxAutoSelectedEvents;
          this.storageService.setStorageValue('coachingConfig', res?.value?.coachingConfig);
        },
        () => {}
      );
  }

  public coachingThresholdsubmit(event: CoachingConfig) {
    this.loader = true;
    this.onSubmit(event);
  }

  public autoCoachingEventsubmit(event: CoachingConfig) {
    this.autoCoachingLoader = true;
    this.onSubmit(event);
  }

  private onSubmit(event: CoachingConfig) {
    this.gtmService.coachingThresholdSubmit();
    this.taggingService
      .updateFleetCoachConfig(event)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => {
          this.taggingService.isUpdatingConfig.next(false);
          this.loader = false;
          this.autoCoachingLoader = false;
        })
      )
      .subscribe(
        (res: PostCoachingConfigResponse) => {
          const {
            eventTypes = [],
            incidentsPer100Miles = DEFAULT_INCIDENTS_PER_100_MILES,
            coachingInterval = DEFAULT_COACHING_INTERVAL,
            minimumEventThreshold = MINIMUM_EVENT_THREASHOLD,
          } = res?.value?.coachingConfigJson || {};

          if (res?.value?.coachingConfigJson?.enableAutoSelection || res?.value?.coachingConfigJson?.maxAutoSelectedEvents) {
            this.enableAutoSelection = res?.value?.coachingConfigJson?.enableAutoSelection;
            this.maxAutoSelectedEvents = res?.value?.coachingConfigJson?.maxAutoSelectedEvents;
          }

          this.eventTypes = [...eventTypes];
          this.incidentsPer100Miles = incidentsPer100Miles;
          this.coachingInterval = coachingInterval;
          this.minimumEventThreshold = minimumEventThreshold;

          this.snachBarService.success(this.translate.instant('dutyTypeConfigurationUpdateSuccess'));
          this.storageService.setStorageValue('coachingConfig', res?.value?.coachingConfigJson);
        },
        () => {
          this.snachBarService.success(this.translate.instant('dutyTypeConfigurationUpdateFailed'));
        }
      );
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
